import type { CSSProperties, ReactElement, ReactNode } from "react";
import { ArtIcon, Glyph, centreOffset, glyphClass, logical, type GlyphName, type IconArt } from "../../ui/kit/index.ts";
import { GLYPH_SIZES } from "../../ui/kit/metrics.ts";
import { cardColours } from "./card-colours.ts";
import type { ItemCardContent } from "./card-words.ts";

/**
 * The item card (P2.8-T46, ADR-125 decision 5; the design's `ItemCard` and `ItemArt` in `ItemSurfaces.jsx`, a visual
 * reference): 172 wide and as tall as its content, a 1 pixel ink edge with a 1 pixel ring in the rarity colour inside
 * it and notched corners; the rarity band, its word in the rarity colour and four pips at its right (none for a
 * Relic); the 52 picture plate with its stepped diamond burst in the rarity colour, the sparks of an Epic, a Legendary
 * or a Relic, and the item's art or glyph in its middle; the name in the rarity colour, the type line and the headline
 * number, its unit on a line of its own when the two do not fit the column (ADR-091's note for P2.8-T57, the review's
 * F3); a rule; the stat rows; the description where the design's flavour line stands, without quotes; and the footer
 * band with the hint. Every rarity colour is written on the card as a custom property (`card-colours.ts`), so
 * `card.css` names no hex. Nothing on it moves (decision 5.5).
 */

/**
 * The card's geometry in logical pixels: its width, the picture plate's side, and the room the art is scaled for
 * inside the plate (ADR-125 decision 5.3: k = floor(48 d / 32), so 1 at d = 1, 3 at d = 2 and 4 at d = 3).
 */
export const ITEM_CARD = { width: 172, plate: 52, artRoom: 48 } as const;

/**
 * Each pip of the band: the design's four, 2 by 5.
 */
const PIP_SLOTS = 4;

/**
 * The burst's four diamonds from the outermost, each its half-diagonal less than the plate's half, the design's.
 */
const BURST_INSETS = [2, 8, 14, 19] as const;

/**
 * The sparks' centres on the plate, the design's: two for an Epic, five for a Legendary or a Relic.
 */
const SPARK_POINTS: Readonly<Record<number, readonly (readonly [number, number])[]>> = {
  2: [
    [8, 8],
    [42, 44],
  ],
  5: [
    [6, 8],
    [44, 6],
    [46, 42],
    [8, 44],
    [26, 3],
  ],
};

/**
 * The picture plate: the burst and the sparks drawn on the logical grid, and the art centred in the room or the glyph
 * centred on the plate.
 *
 * @param props How many `sparks` it draws, the card's `colours`, the item's `art` or null, and its `glyph` or null.
 * @returns The plate.
 */
function Plate({ sparks, colours, art, glyph }: { readonly sparks: number; readonly colours: ReturnType<typeof cardColours>; readonly art: IconArt | null; readonly glyph: GlyphName | null }): ReactElement {
  const side = ITEM_CARD.plate;
  const mid = side / 2;
  const roomAt = centreOffset(side, ITEM_CARD.artRoom);
  return (
    <span className="ot-item-card__plate" data-hud="card-plate" aria-hidden="true" style={{ width: logical(side), height: logical(side) }}>
      <svg className="ot-item-card__burst" viewBox={`0 0 ${side} ${side}`} shapeRendering="crispEdges">
        {BURST_INSETS.map((inset, ring) => {
          const d = mid - inset;
          return <polygon key={inset} points={`${mid},${mid - d} ${mid + d},${mid} ${mid},${mid + d} ${mid - d},${mid}`} fill={colours.burst[ring]} />;
        })}
        {(SPARK_POINTS[sparks] ?? []).map(([x, y]) => (
          <g key={`${x}:${y}`} fill={colours.spark} data-hud="card-spark">
            <rect x={x} y={y - 1} width="1" height="3" />
            <rect x={x - 1} y={y} width="3" height="1" />
          </g>
        ))}
      </svg>
      {art !== null ? <ArtIcon art={art} room={ITEM_CARD.artRoom} at={{ left: roomAt, top: roomAt }} /> : glyph === null ? null : <span className={`ot-item-card__glyph ot-icon ${glyphClass(glyph)}`} data-hud="card-glyph" style={{ left: logical(centreOffset(side, GLYPH_SIZES[glyph])), top: logical(centreOffset(side, GLYPH_SIZES[glyph])) }} />}
    </span>
  );
}

/**
 * The item card.
 *
 * @param props The card's `content`; the item's `art` at the 48 room, or null to draw its `glyph` (null for none);
 *   the footer's `hint`, or null for no hint (a page without a pointer); and `close`, the pinned card's close button
 *   drawn at the band's right, or null; and `extra`, rows the surface showing the card adds after the stat rows (the
 *   bar's time left, P2.8-T55), none unless given.
 * @returns The card's content, inside the element the tooltip places.
 * @example
 * <ItemCard content={itemCard(rules, "minor_healing_potion", 5, false)} art={null} glyph="drop" hint="Right click for actions" close={null} />
 */
export function ItemCard({ content, art, glyph, hint, close, extra = null }: { readonly content: ItemCardContent; readonly art: IconArt | null; readonly glyph: GlyphName | null; readonly hint: string | null; readonly close: ReactNode; readonly extra?: ReactNode }): ReactElement {
  const colours = cardColours(content.rarity);
  const style = { "--ot-card-rarity": colours.rarity, "--ot-card-band": colours.band, "--ot-card-plate-ring": colours.plateRing } as CSSProperties;
  return (
    <span className="ot-item-card" data-hud="card" data-rarity={content.rarity} style={style}>
      <span className="ot-item-card__band" data-hud="card-band">
        <span className="ot-item-card__rarity" data-hud="card-rarity">
          {content.rarityWord}
        </span>
        {content.pips === null ? null : (
          <span className="ot-item-card__pips" data-hud="card-pips" aria-hidden="true">
            {Array.from({ length: PIP_SLOTS }, (_, index) => (
              <i key={index} className={index < (content.pips ?? 0) ? "ot-item-card__pip is-lit" : "ot-item-card__pip"} />
            ))}
          </span>
        )}
        {close}
      </span>
      <span className="ot-item-card__body">
        <span className="ot-item-card__top">
          <Plate sparks={content.sparks} colours={colours} art={art} glyph={glyph} />
          <span className="ot-item-card__titles">
            <span className="ot-item-card__name" data-hud="tip-name">
              {content.name}
            </span>
            <span className="ot-item-card__type" data-hud="card-type">
              {content.typeLine}
            </span>
            {content.headline === null ? null : (
              <span className="ot-item-card__headline" data-hud="card-headline">
                <span className="ot-item-card__big">{content.headline.value}</span>
                <span className="ot-item-card__unit">{content.headline.unit}</span>
              </span>
            )}
          </span>
        </span>
        <span className="ot-item-card__rule" aria-hidden="true" />
        {content.stats.map((stat) => (
          <span key={stat.label} className="ot-item-card__stat" data-hud="card-stat">
            <Glyph name={stat.glyph} />
            <span className="ot-item-card__label">{stat.label}</span>
            <span className={stat.harm ? "ot-item-card__value is-harm" : "ot-item-card__value"}>{stat.value}</span>
          </span>
        ))}
        {extra}
        <span className="ot-item-card__description" data-hud="card-description">
          {content.description}
        </span>
      </span>
      {hint === null ? null : (
        <span className="ot-item-card__footer" data-hud="card-footer">
          <span className="ot-item-card__hint">{hint}</span>
        </span>
      )}
    </span>
  );
}
